import { Head } from "@inertiajs/react";
import { useState } from "react";
import { salonpageVideoMp4Url, salonpageVideoPosterUrl, salonpageVideoThumbnailUrl, encodeSalonpagePathId } from "@/lib/_url";
import { useVideoPlayer } from "@/lib/_use-video-player";
import { SalonpageFooter } from "./_footer";
import { NoroshiReserveCta } from "./_noroshi-reserve-cta";

interface Salon {
  id: string;
  name: string;
  nameKana: string;
}

interface VideoItem {
  videoId: string;
  caption: string;
  tags: string[];
  boost: number;
  orientation?: string | null;
}

interface RelatedVideo {
  videoId: string;
  caption: string;
  orientation?: string | null;
}

interface Meta {
  title: string;
  description: string;
}

interface FeatureToggles {
  noroshiReserve?: boolean | number | null;
}

interface Props {
  salon?: Salon;
  video?: VideoItem | null;
  relatedVideos?: RelatedVideo[];
  featureToggles?: FeatureToggles;
  meta?: Meta;
}

export default function SalonVideo(props: Props) {
  if (!props.salon || !props.video) return <NotFound label="Video not found." />;

  const salon = props.salon;
  const video = props.video;
  const relatedVideos = props.relatedVideos ?? [];
  const meta = props.meta ?? { title: `${salon.name} 動画`, description: `${salon.name}の動画です。` };
  const isLandscape = video.orientation === "landscape";

  return (
    <>
      <Head title={meta.title}>
        <meta name="description" content={meta.description} />
      </Head>

      <div className="min-h-screen bg-gray-100 text-zinc-950 antialiased">
        <main className="mx-auto min-h-screen max-w-[448px] rounded-b-[22px] bg-white shadow-[0_0_24px_rgba(15,23,42,0.06)]">
          <header className="sticky top-0 z-10 border-b border-gray-100 bg-white/95 px-4 py-3 backdrop-blur">
            <a href={`/salons/${encodeSalonpagePathId(salon.id)}`} className="text-xs font-bold text-zinc-400">← サロントップへ</a>
          </header>

          <div className={`bg-black ${isLandscape ? "aspect-video" : "aspect-[3/4]"} w-full`}>
            <VideoPlayer videoId={video.videoId} />
          </div>

          <section className="space-y-4 p-4">
            <div>
              <p className="text-xs font-bold text-zinc-400">{salon.name}</p>
              {video.caption && <p className="mt-2 whitespace-pre-line text-sm leading-6 text-zinc-700">{video.caption}</p>}
              {video.tags.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5" aria-label="動画タグ">
                  {video.tags.map((tag) => (
                    <span key={tag} className="rounded-full bg-gray-100 px-2 py-1 text-[10px] font-medium text-zinc-500">
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
            </div>

            <NoroshiReserveCta
              salonId={salon.id}
              enabled={props.featureToggles?.noroshiReserve}
              className="inline-flex h-11 w-full items-center justify-center rounded-md bg-[#070a18] px-4 text-sm font-bold text-white"
            >
              Web予約する
            </NoroshiReserveCta>
          </section>

          {relatedVideos.length > 0 && (
            <section className="border-t border-gray-100 p-4">
              <h2 className="mb-4 text-xl font-bold tracking-tight">ほかの動画</h2>
              <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {relatedVideos.map((item) => (
                  <a key={item.videoId} href={videoDetailUrl(salon, item)} className="w-32 shrink-0">
                    <img src={salonpageVideoThumbnailUrl(item.videoId)} alt={item.caption || "サロン動画"} className={`w-full rounded-md bg-gray-100 object-cover ${item.orientation === "landscape" ? "aspect-video" : "aspect-[3/4]"}`} loading="lazy" />
                    {item.caption && <p className="mt-2 line-clamp-2 text-xs font-bold leading-5">{item.caption}</p>}
                  </a>
                ))}
              </div>
            </section>
          )}

        </main>
        <SalonpageFooter salon={salon} current="top" />
      </div>
    </>
  );
}

function VideoPlayer({ videoId }: { videoId: string }) {
  const [hasError, setHasError] = useState(false);
  const { videoRef } = useVideoPlayer({
    src: hasError ? null : salonpageVideoMp4Url(videoId),
    poster: salonpageVideoPosterUrl(videoId),
    loop: false,
    muted: false,
  });

  if (hasError) {
    return <div className="grid h-full w-full place-items-center text-xs text-zinc-400">動画を読み込めませんでした</div>;
  }

  return (
    <video
      ref={videoRef}
      controls
      playsInline
      className="h-full w-full object-contain"
      onError={() => setHasError(true)}
    />
  );
}

function NotFound({ label }: { label: string }) {
  return (
    <main className="grid min-h-screen place-items-center bg-gray-100 px-6 text-zinc-900">
      <p className="text-sm text-zinc-400">{label}</p>
    </main>
  );
}

function videoDetailUrl(salon: Salon, video: RelatedVideo) {
  return `/salons/${encodeSalonpagePathId(salon.id)}/videos/${encodeSalonpagePathId(video.videoId)}`;
}
